import React, { useEffect, useRef, useState } from "react";
import { Bell, Check, MessageSquare, X, Clock3, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_META = {
    approval: { icon: Check, className: "bg-emerald-100 text-emerald-700", label: "Preview approved" },
    revision: { icon: MessageSquare, className: "bg-amber-100 text-amber-700", label: "Changes requested" },
    reminder_due: { icon: Clock3, className: "bg-sky-100 text-sky-700", label: "Preview reminder due" },
    cod_confirmed: { icon: Check, className: "bg-blue-100 text-blue-700", label: "COD order confirmed — call to schedule" },
};

/**
 * Bell that shows unread Studio approvals, revision requests and
 * 48h preview reminders for admins. Polls the backend every 30s.
 */
const NotificationsBell = () => {
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [running, setRunning] = useState(false);
    const wrapRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/orders/admin/notifications");
            setItems(data.notifications || []);
            setUnread(data.unread || 0);
        } catch {}
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const markRead = async (id) => {
        try {
            await api.post(`/orders/admin/notifications/${id}/read`);
            load();
        } catch {}
    };

    const markAll = async () => {
        try {
            await api.post(`/orders/admin/notifications/mark-all-read`);
            load();
        } catch {}
    };

    const runReminders = async () => {
        setRunning(true);
        try {
            const { data } = await api.post("/orders/admin/preview-reminders/run");
            const fired = data?.reminders_fired ?? 0;
            if (fired) toast.success(`Sent ${fired} preview reminder${fired === 1 ? "" : "s"}`);
            else toast.message("No previews are due for a reminder just yet.");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="relative" ref={wrapRef} data-testid="admin-notif-wrap">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="Notifications"
                data-testid="admin-notif-bell"
                className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 border border-border shadow-sm hover:bg-white transition"
            >
                <Bell className="w-4 h-4 text-primary" />
                {unread > 0 && (
                    <span
                        data-testid="admin-notif-unread-badge"
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center"
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-[380px] max-w-[92vw] rounded-2xl bg-card border border-border shadow-xl overflow-hidden z-50"
                    data-testid="admin-notif-panel"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
                        <div className="text-sm font-semibold">Studio updates</div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={runReminders}
                                disabled={running}
                                data-testid="admin-notif-run-reminders"
                                className="text-[11px] text-primary hover:underline disabled:opacity-50"
                                title="Send reminder emails + WhatsApp nudges for previews unapproved for 48h"
                            >
                                {running ? "Running…" : "Run reminders"}
                            </button>
                            {unread > 0 && (
                                <button onClick={markAll} className="text-[11px] text-muted-foreground hover:text-foreground" data-testid="admin-notif-mark-all">
                                    Mark all read
                                </button>
                            )}
                            <button onClick={() => setOpen(false)} aria-label="Close" className="text-muted-foreground hover:text-foreground">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="max-h-[440px] overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="p-6 text-center text-xs text-muted-foreground" data-testid="admin-notif-empty">
                                No approvals or revision requests yet.
                            </div>
                        ) : (
                            items.map((n) => {
                                const meta = KIND_META[n.kind] || KIND_META.approval;
                                const Icon = meta.icon;
                                return (
                                    <div
                                        key={n.id}
                                        data-testid={`admin-notif-item-${n.id}`}
                                        className={`px-4 py-3 border-b border-border/50 text-xs flex items-start gap-3 ${n.read ? "opacity-70" : "bg-primary/5"}`}
                                    >
                                        <div className={`mt-0.5 w-7 h-7 rounded-full inline-flex items-center justify-center shrink-0 ${meta.className}`}>
                                            <Icon className="w-3.5 h-3.5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold">
                                                {meta.label}
                                                <span className="text-muted-foreground font-normal"> · {n.customer_name || "Customer"}</span>
                                            </div>
                                            <div className="text-muted-foreground truncate">
                                                {n.order_number} · {n.product_name} {n.size_label ? `(${n.size_label})` : ""}
                                            </div>
                                            {n.note && <div className="mt-1 italic text-foreground/80">"{n.note}"</div>}
                                            <div className="mt-1 flex flex-wrap gap-3 items-center">
                                                <Link
                                                    to="/admin/orders"
                                                    onClick={() => { setOpen(false); markRead(n.id); }}
                                                    className="text-primary underline"
                                                    data-testid={`admin-notif-open-${n.id}`}
                                                >
                                                    Open order
                                                </Link>
                                                {(n.kind === "reminder_due" || n.kind === "cod_confirmed") && n.whatsapp_url && (
                                                    <a
                                                        href={n.whatsapp_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={() => markRead(n.id)}
                                                        data-testid={`admin-notif-wa-${n.id}`}
                                                        className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
                                                    >
                                                        <Send className="w-3 h-3" /> {n.kind === "cod_confirmed" ? "Call/WhatsApp customer" : "Nudge on WhatsApp"}
                                                    </a>
                                                )}
                                                {!n.read && (
                                                    <button onClick={() => markRead(n.id)} className="text-muted-foreground hover:text-foreground">
                                                        Mark read
                                                    </button>
                                                )}
                                                <span className="ml-auto text-[10px] text-muted-foreground">
                                                    {new Date(n.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsBell;
